import { CopyOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import { usePaldockTranslation } from "hooks/usePaldockTranslation.tsx";
import React from "react";
import { toast } from "react-toastify";

import { EmptyCell } from "../EmptyCell/EmptyCell.tsx";

interface UrlCellProps {
    url: string | null | undefined;
    maxWidth?: number | string;
    label?: string;
    /** What the copy icon copies. Defaults to the URL itself. */
    copyValue?: string;
}

export const UrlCell: React.FC<UrlCellProps> = ({ url, maxWidth = 200, label, copyValue }) => {
    const { t } = usePaldockTranslation("common");

    if (!url) return <EmptyCell />;

    const safeUrl = (() => {
        try {
            const parsed = new URL(url);
            return parsed.protocol === "http:" || parsed.protocol === "https:" ? parsed.toString() : null;
        } catch {
            return null;
        }
    })();

    if (!safeUrl) return <EmptyCell />;

    const handleCopy = async (e: React.MouseEvent) => {
        e.stopPropagation();
        try {
            await navigator.clipboard.writeText(copyValue ?? safeUrl);
            toast.success(t("copied_to_clipboard"));
        } catch {
            toast.error(t("errors.something_went_wrong"));
        }
    };

    return (
        <div className="flex items-center gap-1 min-w-0">
            <Tooltip title={safeUrl} placement="topLeft">
                <a
                    href={safeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="truncate text-blue-600 underline hover:text-blue-800"
                    style={{ maxWidth }}
                >
                    {label ?? safeUrl}
                </a>
            </Tooltip>
            <Tooltip title={t("copy_tooltips.click_to_copy")}>
                <button
                    type="button"
                    onClick={handleCopy}
                    className="flex-shrink-0 text-gray-400 hover:text-gray-700 bg-transparent p-0 border-0 cursor-pointer leading-none"
                    aria-label={t("copy")}
                >
                    <CopyOutlined />
                </button>
            </Tooltip>
        </div>
    );
};
